import React from 'react';
import {
    Users,
    BarChart3,
    FileText,
    UserCog,
    Settings,
    ChevronLeft,
    ChevronRight,
    Building2,
} from 'lucide-react';

const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
    { id: 'drivers', label: 'Drivers', icon: Users, path: '/drivers' },
    { id: 'sites', label: 'Sites', icon: Building2, path: '/sites' },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/reports' },
    { id: 'users', label: 'User Management', icon: UserCog, path: '/user-management' },
];

interface SidebarProps {
    collapsed: boolean;
    onCollapse: () => void;
    currentPath: string;
    onNavigate: (path: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse, currentPath, onNavigate }) => {
    return (
        <div className={`flex flex-col h-full transition-all duration-300 ease-in-out flex-shrink-0 print:hidden ${collapsed ? 'w-[52px]' : 'w-52'}`}>

            {/* Logo */}
            <div className="flex items-center justify-center py-5 flex-shrink-0">
                {collapsed ? (
                    <button
                        onClick={() => onNavigate('/dashboard')}
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        <span className="text-white font-bold text-xs leading-none">id</span>
                    </button>
                ) : (
                    <button onClick={() => onNavigate('/dashboard')}>
                        <img
                            src="/logo-horizontal-white.png"
                            alt="IntelliDrive"
                            className="h-16 w-auto"
                        />
                    </button>
                )}
            </div>

            {/* Nav items */}
            <nav className="flex-1 px-1.5 py-2 space-y-0.5">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPath.startsWith(item.path);
                    return (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.path)}
                            title={collapsed ? item.label : undefined}
                            className={`w-full flex items-center h-10 rounded-lg transition-colors duration-150 ${
                                collapsed ? 'justify-center' : 'px-3 gap-3'
                            } ${
                                isActive
                                    ? 'bg-[#69BE28] text-white'
                                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                            {!collapsed && (
                                <span className="text-sm font-medium truncate">{item.label}</span>
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Bottom: settings + collapse toggle */}
            <div className="px-1.5 pb-3 space-y-0.5">
                <button
                    onClick={() => onNavigate('/settings')}
                    title={collapsed ? 'Settings' : undefined}
                    className={`w-full flex items-center h-10 rounded-lg transition-colors ${
                        collapsed ? 'justify-center' : 'px-3 gap-3'
                    } ${
                        currentPath.startsWith('/settings')
                            ? 'bg-[#69BE28] text-white'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                >
                    <Settings className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">Settings</span>}
                </button>
                <button
                    onClick={onCollapse}
                    title={collapsed ? 'Expand' : 'Collapse'}
                    className={`w-full flex items-center h-10 rounded-lg transition-colors text-gray-500 hover:text-gray-200 hover:bg-white/5 ${
                        collapsed ? 'justify-center' : 'px-3 gap-3'
                    }`}
                >
                    {collapsed ? (
                        <ChevronRight className="w-4 h-4" />
                    ) : (
                        <>
                            <ChevronLeft className="w-4 h-4" />
                            <span className="text-sm font-medium">Collapse</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
